import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import { useCustomAlertStore, useProjectMEditStore } from "../../Store/store";
import { patchProjectEdit, postProjectMEdit } from "../../api/ProjectAPI";
import { useLocation } from "react-router-dom";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 380px;
  min-height: 200px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: center;
  gap: 20px;
  padding: 24px 28px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

  @media (max-width: 600px) {
    width: 90%;
    padding: 15px;
    border-radius: 5px;
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  margin-bottom: 20px;
  @media (max-width: 600px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  @media (max-width: 600px) {
    gap: 10px;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 5px;
  @media (max-width: 600px) {
    font-size: 12px;
    margin-bottom: 3px;
  }
`;

const Input = styled.input`
  height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  padding: 0 12px;
  outline: none;
  transition: all 0.2s ease;
  font-size: 14px;

  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }

  &:focus {
    border-color: #4caf50;
    box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.25);
  }
  @media (max-width: 600px) {
    height: 30px;
    font-size: 13px;
    padding: 0 10px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
  @media (max-width: 600px) {
    gap: 5px;
    margin-top: 5px;
  }
`;

const Button = styled.button`
  padding: 10px 18px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;

  &.primary {
    background: #4caf50;
    color: white;

    &:hover {
      background: #45a049;
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.85);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }
  @media (max-width: 600px) {
    padding: 8px 15px;
    font-size: 13px;
    min-width: 60px;
  }
`;

export default function ProjectMEdit({
  isOpen,
  onClose,
  currentName,
  currentDescription,
}) {
  const [projectName, setProjectName] = useState(currentName || "");
  const [projectDescription, setProjectDescription] = useState(
    currentDescription || "",
  );
  const { showSuccessAlert, showErrorAlert } = useCustomAlertStore();
  const { projectManagerId } = useProjectMEditStore();
  const inputRef = useRef(null);
  const nav = useLocation();
  const isTaskPage = nav.pathname.split("/").includes("task");
  const { projectData } = useProjectMEditStore();

  const handleConfirm = useCallback(async () => {
    if (!projectName.trim()) {
      showErrorAlert("입력 오류", "프로젝트 이름을 입력해주세요.");
      return;
    }

    try {
      let response;
      // 페이지 이름에 따라 다른 함수 호출

      if (isTaskPage) {
        response = await patchProjectEdit(
          projectName,
          projectDescription,
          projectData.fileId,
          projectData.accessType,
          projectData.projectId,
        );
      } else {
        response = await postProjectMEdit(
          projectName,
          projectDescription,
          projectManagerId,
        );
      }
      if (response.id) {
        showSuccessAlert("수정 완료", "프로젝트 이름이 수정되었습니다.");
        onClose();
      } else {
        showErrorAlert("수정 실패", "프로젝트 이름 수정에 실패했습니다.");
      }
      onClose();
    } catch (error) {
      onClose();
      showErrorAlert("수정 실패", "프로젝트 이름 수정에 실패했습니다.");
    }
  }, [
    projectName,
    projectDescription,
    onClose,
    showSuccessAlert,
    showErrorAlert,
  ]);

  const handleCancel = useCallback(() => {
    setProjectName(currentName || "");
    setProjectDescription(currentDescription || "");
    onClose();
  }, [currentName, currentDescription, onClose]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    setProjectName(currentName || "");
    setProjectDescription(currentDescription || "");
  }, [currentName, currentDescription]);

  // 엔터키 누르면 확인, 취소 버튼 누르면 취소
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, handleConfirm, handleCancel]);

  // 포커스 처리
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <Title>
          {isTaskPage ? "프로젝트 이름수정" : "프로젝트 매니저 이름수정"}
        </Title>

        <FormContainer>
          <Label htmlFor="projectNameInput">
            {isTaskPage ? "프로젝트 이름" : "프로젝트 매니저 이름"}
          </Label>
          <Input
            ref={inputRef}
            id="projectNameInput"
            type="text"
            placeholder="이름을 입력하세요"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
          />
          <Label htmlFor="projectNameInput">
            {isTaskPage ? "프로젝트 설명" : "프로젝트 매니저 설명"}
          </Label>
          <Input
            id="projectDescriptionInput"
            type="text"
            placeholder="설명을 입력하세요"
            value={projectDescription}
            onChange={(e) => setProjectDescription(e.target.value)}
          />
        </FormContainer>

        <ButtonRow>
          <Button className="secondary" onClick={handleCancel}>
            취소
          </Button>
          <Button className="primary" onClick={handleConfirm}>
            확인
          </Button>
        </ButtonRow>
      </ModalContainer>
    </ModalOverlay>
  );
}
